import { generateMigration } from '@jbroll/rowboat-schema';
import type { SchemaMigration, TableManifest } from '@jbroll/rowboat-shared';
import { APP_VERSION } from './storeNames';

/**
 * One migration per version after the first, each the diff of its manifest against the one before.
 * The control plane migrates a tenant with the same `generateMigration` call and no annotations,
 * so these steps are the server's.
 */
export function migrationChain(manifests: ReadonlyMap<number, TableManifest[]>): SchemaMigration[] {
  const versions = [...manifests.keys()].sort((a, b) => a - b);
  const chain: SchemaMigration[] = [];
  for (let v = versions[0] + 1; v <= versions[versions.length - 1]; v++) {
    const prev = manifests.get(v - 1);
    const next = manifests.get(v);
    if (!prev || !next) throw new Error(`schemaMigrations: no manifest for version ${v}`);
    chain.push(generateMigration(prev, next, v).migration);
  }
  return chain;
}

const files = import.meta.glob<TableManifest[]>('../../shared/manifests/v*.json', {
  eager: true,
  import: 'default',
});

/** shared/manifests, written by `npm run schema:lock` (scripts/schema-snapshot.ts). */
export const SCHEMA_MANIFESTS: ReadonlyMap<number, TableManifest[]> = new Map(
  Object.entries(files).map(([path, manifest]) => {
    const version = /v(\d+)\.json$/.exec(path)?.[1];
    if (!version) throw new Error(`schemaMigrations: ${path} is not named v<N>.json`);
    return [Number(version), manifest];
  }),
);

if (!SCHEMA_MANIFESTS.has(APP_VERSION))
  throw new Error(
    `schemaMigrations: no shared/manifests/v${APP_VERSION}.json for the lock's last version; run npm run schema:lock`,
  );

/** Every local rowboat store opens through this chain; Dexie version 1 is the first snapshot's shape. */
export const MIGRATIONS = migrationChain(SCHEMA_MANIFESTS);
